import { ArrowRight, Check, Clock3, MapPin, Phone } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { LightboxGallery } from "../components/home/LightboxGallery";
import { ProductCard } from "../components/products/ProductCard";
import { ImageSlot } from "../components/shared/ImageSlot";
import { PageMeta } from "../components/shared/PageMeta";
import { SectionHeading } from "../components/shared/SectionHeading";
import { WhatsAppLink } from "../components/shared/WhatsAppLink";
import { mediaAssets } from "../data/assets";
import { highlights } from "../data/highlights";
import { products } from "../data/products";
import { siteConfig } from "../data/siteConfig";
import { safeExternalUrl } from "../lib/whatsapp";

export function HomePage() {
  const directionsUrl = safeExternalUrl(siteConfig.directionsUrl);
  return (
    <>
      <PageMeta
        title="Pastelaria Casagrande | Pastelaria e Encomendas na Marinha Grande"
        description="Descubra a Pastelaria Casagrande na Marinha Grande. Pastelaria, salgados, refeições e encomendas para festas, preparados com sabor e cuidado."
        path="/"
        includeBusinessSchema
      />

      <section className="hero">
        <div className="container hero__grid">
          <div className="hero__content reveal">
            <p className="eyebrow">Sabores que sabem a casa</p>
            <h1>A sua pastelaria de confiança na Marinha Grande</h1>
            <p className="hero__lead">Pastelaria, salgados, refeições e encomendas preparadas com sabor, cuidado e aquele toque familiar.</p>
            <div className="hero__actions">
              <WhatsAppLink>Encomendar pelo WhatsApp</WhatsAppLink>
              <Link to="/produtos" className="button button--secondary">Ver produtos <ArrowRight size={18} aria-hidden="true" /></Link>
            </div>
            <p className="hero__trust">Produtos frescos <span>•</span> Encomendas para festas <span>•</span> Atendimento próximo</p>
          </div>
          <div className="hero__visual reveal">
            <ImageSlot slot="HERO_FACHADA_OU_COMPOSICAO" alt="Pastéis fritos da Pastelaria Casagrande" priority aspect="portrait" />
            <div className="hero__seal" aria-hidden="true"><span>Feito com</span><strong>cuidado</strong></div>
          </div>
        </div>
      </section>

      <section className="highlights" aria-label="Destaques da Casagrande">
        <div className="container highlights__grid">
          {highlights.map(({ title, description, icon: Icon }) => (
            <article key={title}>
              <Icon aria-hidden="true" />
              <div><h2>{title}</h2><p>{description}</p></div>
            </article>
          ))}
        </div>
      </section>

      <section className="section section--surface">
        <div className="container">
          <div className="section-topline">
            <SectionHeading eyebrow="Especialidades da casa" title="Há sempre um sabor à sua espera" description="Descubra algumas das opções que fazem parte da Casagrande. A disponibilidade é confirmada diretamente com a nossa equipa." />
            <Link to="/produtos" className="text-link">Ver todos os produtos <ArrowRight size={17} aria-hidden="true" /></Link>
          </div>
          <div className="products-grid">
            {products.filter((product) => product.featured).map((product) => <ProductCard key={product.id} product={product} />)}
          </div>
        </div>
      </section>

      <section className="section specialty-video-section" aria-labelledby="caldo-cana-title">
        <div className="container specialty-video__grid">
          <figure className="specialty-video__media reveal">
            <video controls playsInline preload="metadata" poster={mediaAssets.caldoCanaVideoPoster}>
              <source src={mediaAssets.caldoCanaVideo} type="video/mp4" />
              O seu navegador não suporta a reprodução deste vídeo.
            </video>
            <figcaption>Um momento real da preparação do caldo de cana na Casagrande.</figcaption>
          </figure>
          <div className="editorial__content reveal">
            <p className="eyebrow">Especialidade da casa</p>
            <h2 id="caldo-cana-title">Caldo de cana fresco, preparado à vista</h2>
            <p>Refrescante e cheio de sabor, o caldo de cana é uma das especialidades que pode encontrar na Casagrande. Veja um momento real da preparação e confirme connosco a disponibilidade.</p>
            <WhatsAppLink message="Olá! Gostaria de consultar a disponibilidade do caldo de cana.">Consultar disponibilidade</WhatsAppLink>
          </div>
        </div>
      </section>

      <section className="section editorial editorial--wine">
        <div className="container editorial__grid">
          <ImageSlot slot="PRODUTO_FEIJOADA" alt="Feijoada da Pastelaria Casagrande" aspect="landscape" />
          <div className="editorial__content reveal">
            <p className="eyebrow">Refeições especiais</p>
            <h2>Há sabores que pedem mesa cheia</h2>
            <p>A nossa feijoada é preparada para quem aprecia comida bem servida, saborosa e feita com cuidado. Consulte a disponibilidade e reserve com antecedência.</p>
            <WhatsAppLink message="Olá! Gostaria de consultar a disponibilidade da feijoada e fazer uma reserva.">Reservar pelo WhatsApp</WhatsAppLink>
          </div>
        </div>
      </section>

      <section className="section section--cream">
        <div className="container editorial__grid editorial__grid--reverse">
          <div className="editorial__content reveal">
            <p className="eyebrow">Encomendas para festas</p>
            <h2>A festa é sua. Os salgados são por nossa conta.</h2>
            <p>Prepare aniversários, reuniões e momentos especiais com salgados preparados por encomenda. Fale connosco para consultar variedades, quantidades e disponibilidade.</p>
            <ul className="check-list">
              {["Variedade de salgados", "Preparação por encomenda", "Ideal para aniversários e eventos", "Atendimento personalizado"].map((item) => <li key={item}><Check aria-hidden="true" />{item}</li>)}
            </ul>
            <WhatsAppLink message="Olá! Gostaria de pedir informações sobre salgados para uma festa.">Pedir informações</WhatsAppLink>
          </div>
          <ImageSlot slot="ENCOMENDAS_FESTAS" alt="Variedade de salgados para festas" aspect="landscape" />
        </div>
      </section>

      <section className="section section--surface">
        <div className="container editorial__grid">
          <ImageSlot slot="EQUIPA_OU_ATENDIMENTO" alt="Cliente a consultar a ementa da Pastelaria Casagrande" aspect="landscape" />
          <div className="editorial__content reveal">
            <p className="eyebrow">A nossa casa</p>
            <h2>Mais do que uma pastelaria</h2>
            <p>Na Casagrande, cada produto é preparado para criar momentos simples e saborosos. Somos uma pastelaria próxima da comunidade, onde o atendimento familiar e a qualidade fazem parte de cada visita.</p>
            <Link to="/sobre" className="button button--secondary">Conhecer a Casagrande <ArrowRight size={18} aria-hidden="true" /></Link>
          </div>
        </div>
      </section>

      <section className="section gallery-section">
        <div className="container">
          <SectionHeading eyebrow="Galeria" title="Um pouco da Casagrande" description="Conheça o espaço e alguns dos sabores preparados na nossa pastelaria através de fotografias reais." align="center" />
          <LightboxGallery limit={8} />
        </div>
      </section>

      <section className="section location-section">
        <div className="container location-grid">
          <div className="location-card reveal">
            <p className="eyebrow">Venha visitar-nos</p>
            <h2>Estamos na Marinha Grande</h2>
            <ul>
              <li><MapPin aria-hidden="true" /><span><strong>Localização</strong>{siteConfig.address}</span></li>
              <li><Phone aria-hidden="true" /><span><strong>Telemóvel e WhatsApp</strong><a href={`tel:+${siteConfig.phoneRaw}`}>{siteConfig.phoneDisplay}</a></span></li>
              <li><Clock3 aria-hidden="true" /><span><strong>Horário</strong>{siteConfig.openingHours}</span></li>
            </ul>
            <div className="location-card__actions">
              {directionsUrl ? <a href={directionsUrl} target="_blank" rel="noopener noreferrer" className="button button--secondary">Obter direções</a> : <p className="config-note">Peça-nos a localização exata pelo WhatsApp.</p>}
              <WhatsAppLink>Falar connosco</WhatsAppLink>
            </div>
          </div>
          <div className="map-slot" role="img" aria-label="Mapa por configurar quando a morada exata estiver disponível">
            <MapPin aria-hidden="true" />
            <strong>Mapa preparado</strong>
            <span>A morada exata será adicionada aqui.</span>
          </div>
        </div>
      </section>

      <section className="final-cta">
        <div className="container final-cta__inner">
          <div><p className="eyebrow">Fale connosco</p><h2>Já sabe o que vai provar hoje?</h2><p>Consulte os produtos disponíveis, refeições e encomendas.</p></div>
          <WhatsAppLink className="button button--light">Falar com a Casagrande</WhatsAppLink>
        </div>
      </section>
    </>
  );
}
